import { Fragment, PropsWithChildren, ReactNode } from "react";
import useScrollY from "@models/interactions/use-scrollY";
import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import { useAtom } from "jotai";
import { showCountdownAtom } from "@models/state/count-down";

interface Props extends PropsWithChildren {
  open: boolean;
  onClose: () => void;
  header?: ReactNode;
  direction?: string;
}

/**
 * A Drawer component that opens on user click.
 * @param open - Boolean state. If `true`, then the drawer opens.
 * @param onClose - Function should set the open state.
 * @param children - React children node.
 */

export function Drawer({ open, onClose, children, header, direction }: Props) {
  const scrollY = useScrollY();
  const [showCountdown, _] = useAtom(showCountdownAtom);

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in duration-200"
          enterFrom="opacity-0 right-0"
          enterTo="opacity-100"
          leave="ease-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0 right-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div
          className={`${
            direction === "right" ? "fixed z-50 inset-0 top-0" : "fixed inset-0"
          }`}
        >
          <div className="absolute inset-0 overflow-hidden">
            <div
              className={classNames("fixed", "inset-y-0", {
                "top-[90px]": scrollY < 80 && showCountdown,
                "right-0 flex w-full sm:w-[512px]": direction === "right",
                "left-0 h-full flex": direction !== "right",
              })}
            >
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom={
                  direction === "right"
                    ? "translate-x-full"
                    : "-translate-x-full"
                }
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo={
                  direction === "right"
                    ? "translate-x-full"
                    : "-translate-x-full"
                }
              >
                <Dialog.Panel className="w-full transform text-left align-middle shadow-xl transition-all antialiased bg-white">
                  {header}

                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

/* Use for associating arialabelledby with the title*/
Drawer.Title = Dialog.Title;
